import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BudgetPage.css';
import { FiTrash2, FiPlus } from 'react-icons/fi';

const API_URL = '/api';

function BudgetPage({ tasks, onReload }) {
  const [budgetData, setBudgetData] = useState(null);
  const [allExpenses, setAllExpenses] = useState([]);
  const [selectedTask, setSelectedTask] = useState('general');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Main d\'œuvre');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const categories = [
    'Main d\'œuvre',
    'Matériaux',
    'Équipement',
    'Transport',
    'Permis & Frais',
    'Autres'
  ];

  useEffect(() => {
    loadBudgetData();
  }, []);

  const loadBudgetData = async () => {
    try {
      const [summaryRes, allExpensesRes] = await Promise.all([
        axios.get(`${API_URL}/budget/summary`),
        axios.get(`${API_URL}/budget/all`)
      ]);
      setBudgetData(summaryRes.data);
      setAllExpenses(allExpensesRes.data);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!amount) {
      alert('Le montant est requis');
      return;
    }

    try {
      // Conversion task_id en nombre ou null
      let taskId = null;
      if (selectedTask !== 'general') {
        taskId = parseInt(selectedTask);
      }

      await axios.post(`${API_URL}/budget`, {
        task_id: taskId,
        amount: parseFloat(amount),
        category,
        description,
        date
      });

      setAmount('');
      setDescription('');
      setDate(new Date().toISOString().split('T')[0]);
      setSelectedTask('general');
      await loadBudgetData();
    } catch (err) {
      console.error('Erreur complète:', err.response || err);
      alert('Erreur lors de l\'ajout de la dépense: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/budget/${id}`);
      await loadBudgetData();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      'Main d\'œuvre': '#3b82f6',
      'Matériaux': '#10b981',
      'Équipement': '#f59e0b',
      'Transport': '#8b5cf6',
      'Permis & Frais': '#ef4444',
      'Autres': '#6b7280'
    };
    return colors[cat] || '#6b7280';
  };

  return (
    <div className="budget-page">
      <h2>💰 Gestion du Budget</h2>

      {budgetData && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-label">Total Dépensé</div>
            <div className="card-value">{budgetData.summary?.total_spent?.toFixed(2) || 0} €</div>
          </div>
          <div className="summary-card">
            <div className="card-label">Catégories</div>
            <div className="card-value">{budgetData.summary?.categories || 0}</div>
          </div>
          {budgetData.byCategory && budgetData.byCategory.length > 0 && (
            <div className="summary-card">
              <div className="card-label">Catégorie Principale</div>
              <div className="card-value">{budgetData.byCategory[0].category}</div>
              <div className="card-detail">{budgetData.byCategory[0].total?.toFixed(2) || 0} €</div>
            </div>
          )}
        </div>
      )}

      <div className="budget-content">
        <div className="budget-form-section">
          <h3>Ajouter une dépense</h3>
          <form onSubmit={handleAddExpense} className="expense-form">
            <div className="form-group">
              <label>Lié à une tâche (optionnel)</label>
              <select 
                value={selectedTask} 
                onChange={(e) => setSelectedTask(e.target.value)}
              >
                <option value="general">➕ Dépense générale (non liée)</option>
                {tasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Montant (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label>Catégorie</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Description (optionnel)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex: Ciment, planches..."
                />
              </div>
            </div>

            <button type="submit" className="btn-add-expense">
              <FiPlus /> Ajouter la dépense
            </button>
          </form>
        </div>

        <div className="budget-display-section">
          <h3>Dépenses par catégorie</h3>
          {budgetData?.byCategory && budgetData.byCategory.length > 0 ? (
            <div className="categories-list">
              {budgetData.byCategory.map(cat => (
                <div key={cat.category} className="category-item">
                  <div className="category-info">
                    <span 
                      className="category-color" 
                      style={{ backgroundColor: getCategoryColor(cat.category) }}
                    />
                    <div className="category-details">
                      <div className="category-name">{cat.category}</div>
                      <div className="category-count">{cat.count} dépense(s)</div>
                    </div>
                  </div>
                  <div className="category-amount">{cat.total?.toFixed(2) || 0} €</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">Aucune dépense enregistrée</p>
          )}
        </div>
      </div>

      <div className="expenses-section">
        <h3>Historique des dépenses</h3>
        {allExpenses.length === 0 ? (
          <p className="empty-text">Aucune dépense</p>
        ) : (
          <div className="expenses-list">
            {allExpenses.map(expense => (
              <div key={expense.id} className="expense-item">
                <div className="expense-header">
                  <div className="expense-type">
                    <span 
                      className="type-badge" 
                      style={{ backgroundColor: getCategoryColor(expense.category) }}
                    >
                      {expense.category}
                    </span>
                    {expense.task_title && (
                      <span className="task-ref">{expense.task_title}</span>
                    )}
                  </div>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeleteExpense(expense.id)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
                
                {expense.description && (
                  <p className="expense-desc">{expense.description}</p>
                )}
                
                <div className="expense-footer">
                  <span className="expense-date">{expense.date}</span>
                  <span className="expense-amount">{expense.amount?.toFixed(2) || 0} €</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BudgetPage;
